import React, { useState, useEffect } from 'react';
import Joyride, { Step } from 'react-joyride';

interface OnboardingJoyrideProps {
  run: boolean;
  onFinish: () => void;
}

const OnboardingJoyride: React.FC<OnboardingJoyrideProps> = ({ run, onFinish }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);

  // Wait for all target elements to be available
  useEffect(() => {
    if (!run) {
      setIsReady(false);
      return;
    }

    const checkTargets = () => {
      // Check basic required elements that should always be present
      const basicTargets = [
        '[data-tour="navigation"]',
        '[data-tour="help-button"]'
      ];

      const basicTargetsExist = basicTargets.every(selector => {
        const element = document.querySelector(selector);
        return element !== null;
      });

      if (basicTargetsExist) {
        setIsReady(true);
      } else {
        // Retry after a short delay
        setTimeout(checkTargets, 100);
      }
    };

    // Small delay to ensure components are mounted
    setTimeout(checkTargets, 250);
  }, [run]);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-super-dark-purple mb-4">Welcome to JARVIS RCSA!</h2>
          <p className="text-gray-600 mb-4">
            Let's walk through your first risk assessment project. I'll show you how to create a project, 
            upload documents, and generate AI-powered analysis in just a few steps.
          </p>
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-super-dark-purple mb-2">What we'll cover:</h4>
            <div className="text-left space-y-2 text-sm text-gray-700">
              <div className="flex items-center">
                <span className="text-electric-blue mr-2">1.</span>
                Create your first project
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2">2.</span>
                Upload policy documents
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2">3.</span>
                Generate AI analysis
              </div>
              <div className="flex items-center">
                <span className="text-electric-blue mr-2">4.</span>
                Review results and insights
              </div>
            </div>
          </div>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="new-project-button"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Step 1: Create Your First Project</h3>
          <p className="text-gray-600 mb-3">
            Every risk assessment starts with a project. Projects help you organize documents, 
            track analysis progress, and manage different assessments separately.
          </p>
          <div className="bg-blue-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-blue-800">
              <strong>Try it:</strong> Click the "New Project" button to create your first project. 
              You can name it something like "Banking Risk Assessment" or "Operational Review".
            </p>
          </div>
        </div>
      ),
      placement: 'bottom'
    },
    {
      target: '[data-tour="navigation"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Navigation Overview</h3>
          <p className="text-gray-600 mb-3">
            The sidebar contains all the main sections you'll use. Let's focus on the key workflow areas:
          </p>
          <div className="space-y-2 mt-3">
            <div className="flex items-center bg-blue-50 rounded p-2">
              <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
              <span className="text-sm text-blue-800"><strong>Dashboard</strong> - Project overview and creation</span>
            </div>
            <div className="flex items-center bg-green-50 rounded p-2">
              <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
              <span className="text-sm text-green-800"><strong>Documents</strong> - Upload and manage files</span>
            </div>
            <div className="flex items-center bg-purple-50 rounded p-2">
              <div className="w-3 h-3 bg-purple-500 rounded mr-2"></div>
              <span className="text-sm text-purple-800"><strong>Analysis</strong> - AI insights and reports</span>
            </div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-yellow-800">
              <strong>Next:</strong> After creating your project, we'll go to Documents to upload files.
            </p>
          </div>
        </div>
      ),
      placement: 'right'
    },
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Step 4: Document Upload Process</h3>
          <p className="text-gray-600 mb-3">
            The next step in your workflow is uploading documents. You can navigate to the Documents section 
            when you're ready to start uploading your policies, procedures, and other files.
          </p>
          <div className="bg-green-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-green-800 mb-2">What you'll upload:</h4>
            <div className="text-sm text-green-700 space-y-1">
              <div>• <strong>Policies:</strong> Risk policies, compliance documents</div>
              <div>• <strong>Procedures:</strong> SOPs, process documents</div>
              <div>• <strong>Risk Frameworks:</strong> COSO, ISO 31000, etc.</div>
              <div>• <strong>Reports:</strong> Audit reports, assessments</div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-blue-800">
              <strong>Next:</strong> Go to Documents → Upload Documents → Select your files
            </p>
          </div>
        </div>
      ),
      placement: 'center'
    },
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Step 5: AI Analysis Process</h3>
          <p className="text-gray-600 mb-3">
            After uploading documents, you'll go to the Analysis section to generate AI-powered insights. 
            JARVIS will analyze your content and create comprehensive reports.
          </p>
          <div className="bg-purple-50 rounded-lg p-4 mt-3">
            <h4 className="font-semibold text-purple-800 mb-2">What JARVIS generates:</h4>
            <div className="text-sm text-purple-700 space-y-1">
              <div>• <strong>Process Maps:</strong> Visual workflow diagrams</div>
              <div>• <strong>Risk Analysis:</strong> Identified risks and controls</div>
              <div>• <strong>SOPs:</strong> Standard operating procedures</div>
              <div>• <strong>Compliance Mapping:</strong> Framework alignment</div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-blue-800">
              <strong>Process:</strong> Go to Analysis → Click "Analyze New Documents" → Review results
            </p>
          </div>
        </div>
      ),
      placement: 'center'
    },
    {
      target: '[data-tour="help-button"]',
      content: (
        <div>
          <h3 className="text-lg font-bold text-super-dark-purple mb-2">Need Help Anytime?</h3>
          <p className="text-gray-600 mb-3">
            You can always replay this tutorial or get help by clicking the Help button in the header.
          </p>
          <div className="bg-yellow-50 rounded-lg p-3 mt-3">
            <p className="text-sm text-yellow-800">
              <strong>Remember:</strong> This tutorial is always available when you need a refresher!
            </p>
          </div>
        </div>
      ),
      placement: 'bottom-start'
    },
    {
      target: 'body',
      content: (
        <div className="text-center">
          <h2 className="text-2xl font-bold text-super-dark-purple mb-4">You're Ready to Go! 🎉</h2>
          <p className="text-gray-600 mb-4">
            You now know the complete JARVIS RCSA workflow. Start with creating a project, 
            upload your documents, and let AI transform your risk assessment process.
          </p>
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-gray-800 mb-2">Your Next Steps:</h4>
            <div className="text-left space-y-2 text-sm text-gray-700">
              <div className="flex items-center">
                <span className="text-green-500 mr-2">1️⃣</span>
                Create your first real project
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2">2️⃣</span>
                Upload your policy or procedure documents
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2">3️⃣</span>
                Run AI analysis to generate insights
              </div>
              <div className="flex items-center">
                <span className="text-green-500 mr-2">4️⃣</span>
                Explore process maps, SOPs, and risk analysis
              </div>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Ready to revolutionize your risk assessment workflow!
          </div>
        </div>
      ),
      placement: 'center',
    }
  ];

  const handleJoyrideCallback = (data: any) => {
    const { status, type, index, action } = data;

    if (status === 'finished' || status === 'skipped') {
      setStepIndex(0);
      onFinish();
    } else if (type === 'step:after') {
      setStepIndex(index + 1);
    } else if (status === 'error') {
      // Skip to next step if current target is not found
      setStepIndex(index + 1);
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous={true}
      run={run && isReady}
      scrollToFirstStep={true}
      showProgress={true}
      showSkipButton={true}
      stepIndex={stepIndex}
      steps={steps}
      styles={{
        options: {
          primaryColor: '#57C8F4',
          textColor: '#333',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 10000,
        }
      }}
      locale={{
        back: 'Previous',
        close: 'Close',
        last: 'Get Started!',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
};

export default OnboardingJoyride;